import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { createLogger, LOG_NAMESPACES } from '@/utils/logger';
import { createDataset, deleteDataset, getAllDatasets, getDatasetById, updateDataset } from './datasets.api';

const logger = createLogger(LOG_NAMESPACES.API);
const apiLogger = createLogger(LOG_NAMESPACES.API);

// Query Keys
export const datasetsQueryKeys = {
  all: ['datasets'] as const,
  list: (spaceId?: string) => [...datasetsQueryKeys.all, 'list', spaceId] as const,
  detail: (id?: string) => [...datasetsQueryKeys.all, 'detail', id] as const,
};

export const useDatasets = (id?: string) => {
  return useQuery({
    queryKey: datasetsQueryKeys.list(id),
    queryFn: async () => {
      logger.info('Fetching all datasets', { id });
      const result = await getAllDatasets(id);
      logger.info('Datasets fetched successfully', { count: result?.length || 0 });
      return result;
    },
    staleTime: 1000 * 60 * 5,
    retry: 2,
    enabled: !!id,
  });
};

export const useDatasetById = (id?: string) => {
  return useQuery({
    queryKey: datasetsQueryKeys.detail(id),
    queryFn: async () => {
      logger.info('Fetching dataset by id', { id });
      const result = await getDatasetById(id);
      logger.info('Dataset fetched successfully', { id });
      return result;
    },
    staleTime: 1000 * 60 * 5,
    retry: 2,
    enabled: !!id,
  });
};

export const useCreateDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      logger.info('Creating dataset', data);
      return createDataset(data);
    },
    onError: (err) => {
      logger.error('Error creating dataset', err);
      apiLogger.api('POST', '/chaincode/dataset', {
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      notification.error({
        message: err.message || "We couldn't create the dataset",
        className: 'custom-error-notification bold-notification-message',
        pauseOnHover: true,
      });
    },
    onSuccess: (data) => {
      logger.info('Dataset created successfully', data);
      apiLogger.api('POST', '/chaincode/dataset', { success: true });
      queryClient.invalidateQueries({ queryKey: datasetsQueryKeys.all });
      notification.success({
        message: 'Dataset created successfully',
        className: 'custom-success-notification bold-notification-message',
        pauseOnHover: true,
      });
    },
  });
};

export const useUpdateDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { id: string; name: string }) => {
      logger.info('Updating dataset', data);
      return updateDataset(data);
    },
    onError: (err) => {
      logger.error('Error updating dataset', err);
      apiLogger.api('PATCH', '/chaincode/dataset', {
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      notification.error({
        message: err?.message || "We couldn't update the dataset",
        className: 'custom-error-notification bold-notification-message',
        pauseOnHover: true,
      });
    },
    onSuccess: (data, variables) => {
      logger.info('Dataset updated successfully', data);
      apiLogger.api('PATCH', `/chaincode/dataset/${variables.id}`, { success: true });
      queryClient.invalidateQueries({ queryKey: datasetsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: datasetsQueryKeys.detail(variables.id) });
      notification.success({
        message: 'Dataset updated successfuly',
        className: 'custom-success-notification bold-notification-message',
        pauseOnHover: true,
      });
    },
  });
};

export const useDeleteDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      logger.info('Deleting dataset', { id });
      return deleteDataset(id);
    },
    onError: (err) => {
      logger.error('Error deleting dataset', err);
      apiLogger.api('DELETE', '/chaincode/dataset', {
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      notification.error({
        message: err?.message || 'Could not delete the dataset.',
        className: 'custom-error-notification bold-notification-message',
        pauseOnHover: true,
      });
    },
    onSuccess: (data, variables) => {
      logger.info('Dataset deleted successfully', data);
      apiLogger.api('DELETE', `/chaincode/dataset/${variables}`, { success: true });
      queryClient.invalidateQueries({ queryKey: datasetsQueryKeys.all });
      notification.success({
        message: 'Dataset deleted successfully!',
        className: 'custom-success-notification bold-notification-message',
        pauseOnHover: true,
      });
    },
  });
};
